/**
 * CredencialesView.js - Panel principal de gestión de credenciales
 *
 * Contiene:
 * - Lista de plantillas (Mis Plantillas)
 * - Editor de plantilla (drag & drop)
 * - Generador de credenciales (batch)
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Rnd } from 'react-rnd';
import { useCredenciales } from '../hooks/useCredenciales';
import StickerGenerator from './StickerGenerator';

// Dimensiones CR80 a 300dpi para preview
const CR80_PREVIEW = {
  width: 506,  // Mitad de 1012 para preview
  height: 319  // Mitad de 638 para preview
};

// Factor de escala del preview
const PREVIEW_SCALE = 0.5;
const STICKER_SHEET_PREVIEW_SCALE = 0.2; // A4 es grande, necesita más reducción

// Calcular dimensiones de preview según tipo de plantilla
const getPreviewDimensions = (plantilla) => {
  if (!plantilla?.dimensiones) return CR80_PREVIEW;

  // Para sticker_sheet (A4), usamos escala más pequeña
  const isStickerSheet = plantilla.tipo === 'sticker_sheet';
  const scale = isStickerSheet ? STICKER_SHEET_PREVIEW_SCALE : PREVIEW_SCALE;

  return {
    width: plantilla.dimensiones.ancho * scale,
    height: plantilla.dimensiones.alto * scale,
    scale: scale
  };
};

// Verificar si es plantilla de sticker
const isSticker = (plantilla) => {
  return plantilla?.categoria === 'sticker' || plantilla?.tipo?.startsWith('sticker');
};

function CredencialesView({ addToHistory }) {
  // Estado de navegación interna
  const [subView, setSubView] = useState('lista'); // lista | editor | generar
  const [editingId, setEditingId] = useState(null);
  const [showNewModal, setShowNewModal] = useState(false);

  // Hook de credenciales
  const {
    plantillas,
    plantillaActual,
    loading,
    error,
    loadPlantillas,
    loadPlantilla,
    crearPlantilla,
    actualizarPlantilla,
    subirImagen,
    eliminarPlantilla,
    regenerarUrls,
    generarPDF,
    setPlantillaActual,
    setError
  } = useCredenciales();

  // Estado para regeneración de URLs
  const [regenerando, setRegenerando] = useState(false);

  // Cargar plantillas al montar
  useEffect(() => {
    loadPlantillas();
  }, [loadPlantillas]);

  // Manejar navegación a editor
  const handleEditPlantilla = useCallback(async (id) => {
    const plantilla = await loadPlantilla(id);
    if (plantilla) {
      setEditingId(id);
      setSubView('editor');
      addToHistory?.(`Editando plantilla: ${plantilla.nombre}`);
    }
  }, [loadPlantilla, addToHistory]);

  // Manejar eliminación
  const handleDelete = useCallback(async (id, nombre) => {
    if (window.confirm(`¿Eliminar la plantilla "${nombre}"? Esta acción no se puede deshacer.`)) {
      const success = await eliminarPlantilla(id);
      if (success) {
        addToHistory?.(`Plantilla eliminada: ${nombre}`);
      }
    }
  }, [eliminarPlantilla, addToHistory]);

  // Regenerar URLs de imágenes
  const handleRegenerarUrls = useCallback(async () => {
    if (!window.confirm('¿Regenerar URLs de todas las imágenes de plantillas?\n\nUsa esta opción si las imágenes no cargan correctamente.')) {
      return;
    }

    setRegenerando(true);
    const result = await regenerarUrls();
    setRegenerando(false);

    if (result.success) {
      addToHistory?.(`URLs regeneradas: ${result.actualizadas} plantillas`);
      alert(`URLs regeneradas exitosamente.\n\n${result.actualizadas} plantillas actualizadas\n${result.errores} errores`);
    } else {
      alert(`Error regenerando URLs: ${result.error}`);
    }
  }, [regenerarUrls, addToHistory]);

  // Renderizar según sub-vista
  if (subView === 'editor' && editingId) {
    return (
      <PlantillaEditor
        plantilla={plantillaActual}
        loading={loading}
        onSave={async (campos) => {
          const result = await actualizarPlantilla(editingId, { campos });
          if (result) {
            addToHistory?.(`Plantilla guardada: ${plantillaActual.nombre}`);
          }
          return result;
        }}
        onUploadImage={async (file) => {
          const result = await subirImagen(editingId, file);
          if (result) {
            addToHistory?.(`Imagen subida para: ${plantillaActual.nombre}`);
          }
          return result;
        }}
        onBack={() => {
          setSubView('lista');
          setEditingId(null);
          setPlantillaActual(null);
        }}
      />
    );
  }

  if (subView === 'generar') {
    return (
      <GenerarCredenciales
        plantillas={plantillas}
        loading={loading}
        onGenerar={async (frenteId, reversoId, folios) => {
          const result = await generarPDF(frenteId, reversoId, folios);
          if (result.success) {
            addToHistory?.(`PDF generado: ${result.generadas} credenciales`);
          }
          return result;
        }}
        onBack={() => setSubView('lista')}
      />
    );
  }

  if (subView === 'stickers') {
    return (
      <StickerGenerator
        onBack={() => setSubView('lista')}
        addToHistory={addToHistory}
      />
    );
  }

  // Vista de lista de plantillas
  return (
    <div className="credenciales-view">
      {/* Header */}
      <div
        className="d-flex justify-content-between align-items-center"
        style={{ marginBottom: 'var(--space-5)' }}
      >
        <h5
          style={{
            fontSize: 'var(--font-size-xl)',
            fontWeight: 'var(--font-weight-bold)',
            color: 'var(--gray-900)',
            margin: 0,
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--space-2)'
          }}
        >
          <i className="bi bi-credit-card-2-front" style={{ color: 'var(--feature-primary)' }}></i>
          Credenciales para Impresión
        </h5>

        <div className="d-flex gap-2">
          <button
            className="btn"
            onClick={() => setSubView('generar')}
            style={{
              backgroundColor: 'var(--feature-primary)',
              color: 'var(--white)',
              fontWeight: 'var(--font-weight-semibold)',
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--space-2)'
            }}
          >
            <i className="bi bi-file-pdf"></i>
            Generar PDF
          </button>
          <button
            className="btn"
            onClick={() => setSubView('stickers')}
            style={{
              backgroundColor: 'var(--gray-700)',
              color: 'var(--white)',
              fontWeight: 'var(--font-weight-semibold)',
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--space-2)'
            }}
          >
            <i className="bi bi-stickies"></i>
            Generar Stickers
          </button>
          <button
            className="btn"
            onClick={() => setShowNewModal(true)}
            style={{
              backgroundColor: 'transparent',
              color: 'var(--feature-primary)',
              border: '1px solid var(--feature-primary)',
              fontWeight: 'var(--font-weight-semibold)',
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--space-2)'
            }}
          >
            <i className="bi bi-plus-lg"></i>
            Nueva Plantilla
          </button>
          <button
            className="btn"
            onClick={handleRegenerarUrls}
            disabled={regenerando || loading}
            title="Regenerar URLs de imágenes (usar si las imágenes no cargan)"
            style={{
              backgroundColor: 'transparent',
              color: 'var(--gray-600)',
              border: '1px solid var(--gray-300)',
              padding: '0.375rem 0.5rem'
            }}
          >
            {regenerando ? (
              <span className="spinner-border spinner-border-sm"></span>
            ) : (
              <i className="bi bi-arrow-clockwise"></i>
            )}
          </button>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div
          className="alert alert-danger d-flex align-items-center"
          style={{ marginBottom: 'var(--space-4)' }}
        >
          <i className="bi bi-exclamation-triangle-fill me-2"></i>
          {error}
          <button
            type="button"
            className="btn-close ms-auto"
            onClick={() => setError(null)}
          ></button>
        </div>
      )}

      {/* Loading */}
      {loading && plantillas.length === 0 ? (
        <div className="text-center" style={{ padding: 'var(--space-12) 0' }}>
          <div
            className="spinner-border"
            role="status"
            style={{ color: 'var(--feature-primary)' }}
          >
            <span className="visually-hidden">Cargando plantillas...</span>
          </div>
        </div>
      ) : plantillas.length === 0 ? (
        <div
          className="text-center"
          style={{
            padding: 'var(--space-12) 0',
            color: 'var(--gray-500)'
          }}
        >
          <i
            className="bi bi-credit-card-2-front"
            style={{ fontSize: '3rem', color: 'var(--gray-400)' }}
          ></i>
          <p style={{
            marginTop: 'var(--space-3)',
            fontSize: 'var(--font-size-lg)'
          }}>
            No hay plantillas creadas
          </p>
          <button
            className="btn mt-3"
            onClick={() => setShowNewModal(true)}
            style={{
              backgroundColor: 'var(--feature-primary)',
              color: 'var(--white)',
              fontWeight: 'var(--font-weight-semibold)'
            }}
          >
            <i className="bi bi-plus-lg me-2"></i>
            Crear primera plantilla
          </button>
        </div>
      ) : (
        <div className="row">
          {plantillas.map(plantilla => (
            <div key={plantilla.id} className="col-12 col-md-6 col-lg-4 mb-3">
              <PlantillaCard
                plantilla={plantilla}
                onEdit={() => handleEditPlantilla(plantilla.id)}
                onDelete={() => handleDelete(plantilla.id, plantilla.nombre)}
              />
            </div>
          ))}
        </div>
      )}

      {/* Modal Nueva Plantilla */}
      {showNewModal && (
        <NuevaPlantillaModal
          onClose={() => setShowNewModal(false)}
          onCreate={async (nombre, tipo) => {
            const result = await crearPlantilla(nombre, tipo);
            if (result) {
              setShowNewModal(false);
              addToHistory?.(`Plantilla creada: ${nombre}`);
            }
            return result;
          }}
          loading={loading}
        />
      )}
    </div>
  );
}

// ============================================
// COMPONENTE: PlantillaCard
// ============================================
function PlantillaCard({ plantilla, onEdit, onDelete }) {
  return (
    <div
      className="card h-100"
      style={{
        border: '1px solid var(--gray-200)',
        borderRadius: 'var(--radius-lg)',
        transition: 'var(--transition-base)',
        boxShadow: 'var(--shadow-sm)',
        overflow: 'hidden'
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.borderColor = 'var(--feature-accent)';
        e.currentTarget.style.boxShadow = 'var(--shadow-md)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.borderColor = 'var(--gray-200)';
        e.currentTarget.style.boxShadow = 'var(--shadow-sm)';
      }}
    >
      {/* Preview de imagen */}
      <div
        style={{
          height: '120px',
          backgroundColor: 'var(--gray-100)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'hidden'
        }}
      >
        {plantilla.imagenUrl ? (
          <img
            src={plantilla.imagenUrl}
            alt={plantilla.nombre}
            style={{
              maxWidth: '100%',
              maxHeight: '100%',
              objectFit: 'contain'
            }}
          />
        ) : (
          <i
            className="bi bi-image"
            style={{ fontSize: '2rem', color: 'var(--gray-400)' }}
          ></i>
        )}
      </div>

      <div className="card-body" style={{ padding: 'var(--space-3)' }}>
        <h6
          style={{
            fontSize: 'var(--font-size-base)',
            fontWeight: 'var(--font-weight-semibold)',
            color: 'var(--gray-900)',
            marginBottom: 'var(--space-2)'
          }}
        >
          {plantilla.nombre}
        </h6>

        <div className="d-flex gap-2 mb-3">
          <span
            className="badge"
            style={{
              backgroundColor: plantilla.tipo === 'frente'
                ? 'var(--feature-primary)'
                : 'var(--gray-600)',
              color: 'var(--white)',
              fontSize: 'var(--font-size-xs)',
              padding: 'var(--space-1) var(--space-2)',
              borderRadius: 'var(--radius-full)'
            }}
          >
            {plantilla.tipo === 'frente' ? 'Frente' : 'Reverso'}
          </span>

          <span
            className="badge"
            style={{
              backgroundColor: 'var(--gray-200)',
              color: 'var(--gray-700)',
              fontSize: 'var(--font-size-xs)',
              padding: 'var(--space-1) var(--space-2)',
              borderRadius: 'var(--radius-full)'
            }}
          >
            {plantilla.campos?.length || 0} campos
          </span>
        </div>

        <div className="d-flex gap-2">
          <button
            className="btn btn-sm flex-grow-1"
            onClick={onEdit}
            style={{
              backgroundColor: 'var(--feature-primary)',
              color: 'var(--white)',
              fontWeight: 'var(--font-weight-semibold)',
              fontSize: 'var(--font-size-sm)'
            }}
          >
            <i className="bi bi-pencil me-1"></i>
            Editar
          </button>
          <button
            className="btn btn-sm"
            onClick={onDelete}
            style={{
              backgroundColor: 'transparent',
              color: 'var(--color-danger)',
              border: '1px solid var(--color-danger)',
              fontSize: 'var(--font-size-sm)'
            }}
          >
            <i className="bi bi-trash"></i>
          </button>
        </div>
      </div>
    </div>
  );
}

// ============================================
// COMPONENTE: NuevaPlantillaModal
// ============================================
function NuevaPlantillaModal({ onClose, onCreate, loading }) {
  const [nombre, setNombre] = useState('');
  const [categoria, setCategoria] = useState('credencial');
  const [tipo, setTipo] = useState('frente');

  // Actualizar tipo cuando cambia la categoría
  const handleCategoriaChange = (nuevaCategoria) => {
    setCategoria(nuevaCategoria);
    if (nuevaCategoria === 'credencial') {
      setTipo('frente');
    } else {
      setTipo('sticker_sheet'); // Solo un tipo de sticker: hoja completa
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!nombre.trim()) return;
    await onCreate(nombre.trim(), tipo);
  };

  return (
    <div
      className="modal d-block"
      style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
      onClick={onClose}
    >
      <div
        className="modal-dialog modal-dialog-centered"
        onClick={e => e.stopPropagation()}
      >
        <div className="modal-content" style={{ borderRadius: 'var(--radius-lg)' }}>
          <div className="modal-header" style={{ borderBottom: '1px solid var(--gray-200)' }}>
            <h5 className="modal-title" style={{ fontWeight: 'var(--font-weight-semibold)' }}>
              <i className="bi bi-plus-circle me-2" style={{ color: 'var(--feature-primary)' }}></i>
              Nueva Plantilla
            </h5>
            <button type="button" className="btn-close" onClick={onClose}></button>
          </div>

          <form onSubmit={handleSubmit}>
            <div className="modal-body">
              <div className="mb-3">
                <label className="form-label" style={{ fontWeight: 'var(--font-weight-semibold)' }}>
                  Nombre de la plantilla
                </label>
                <input
                  type="text"
                  className="form-control"
                  value={nombre}
                  onChange={e => setNombre(e.target.value)}
                  placeholder="Ej: Premium Frente, Sticker Redondo ES..."
                  style={{ borderColor: 'var(--gray-300)' }}
                  autoFocus
                />
              </div>

              {/* Selector de categoría */}
              <div className="mb-3">
                <label className="form-label" style={{ fontWeight: 'var(--font-weight-semibold)' }}>
                  Categoría
                </label>
                <div className="d-flex gap-2">
                  <button
                    type="button"
                    className={`btn flex-fill ${categoria === 'credencial' ? '' : 'btn-outline-secondary'}`}
                    onClick={() => handleCategoriaChange('credencial')}
                    style={categoria === 'credencial' ? {
                      backgroundColor: 'var(--feature-primary)',
                      color: 'white',
                      border: 'none'
                    } : {}}
                  >
                    <i className="bi bi-credit-card-2-front me-2"></i>
                    Credencial
                  </button>
                  <button
                    type="button"
                    className={`btn flex-fill ${categoria === 'sticker' ? '' : 'btn-outline-secondary'}`}
                    onClick={() => handleCategoriaChange('sticker')}
                    style={categoria === 'sticker' ? {
                      backgroundColor: 'var(--feature-primary)',
                      color: 'white',
                      border: 'none'
                    } : {}}
                  >
                    <i className="bi bi-stickies me-2"></i>
                    Sticker
                  </button>
                </div>
              </div>

              {/* Tipos de credencial */}
              {categoria === 'credencial' && (
                <div className="mb-3">
                  <label className="form-label" style={{ fontWeight: 'var(--font-weight-semibold)' }}>
                    Tipo de cara
                  </label>
                  <div className="d-flex gap-3">
                    <div className="form-check">
                      <input
                        className="form-check-input"
                        type="radio"
                        id="tipoFrente"
                        checked={tipo === 'frente'}
                        onChange={() => setTipo('frente')}
                      />
                      <label className="form-check-label" htmlFor="tipoFrente">
                        Frente
                      </label>
                    </div>
                    <div className="form-check">
                      <input
                        className="form-check-input"
                        type="radio"
                        id="tipoReverso"
                        checked={tipo === 'reverso'}
                        onChange={() => setTipo('reverso')}
                      />
                      <label className="form-check-label" htmlFor="tipoReverso">
                        Reverso
                      </label>
                    </div>
                  </div>
                </div>
              )}

              {/* Info de sticker sheet */}
              {categoria === 'sticker' && (
                <div className="mb-3">
                  <div
                    className="p-3"
                    style={{
                      backgroundColor: 'var(--gray-50)',
                      borderRadius: 'var(--radius-md)',
                      border: '1px solid var(--gray-200)'
                    }}
                  >
                    <div style={{ fontWeight: 'var(--font-weight-semibold)', marginBottom: '4px' }}>
                      <i className="bi bi-file-earmark-image me-2"></i>
                      Hoja de Stickers A4
                    </div>
                    <small className="text-muted d-block">
                      210×297mm (2480×3508px a 300dpi)
                    </small>
                    <small className="text-muted d-block mt-1">
                      Sube tu diseño completo y posiciona hasta 16 códigos QR
                    </small>
                  </div>
                </div>
              )}
            </div>

            <div className="modal-footer" style={{ borderTop: '1px solid var(--gray-200)' }}>
              <button type="button" className="btn btn-secondary" onClick={onClose}>
                Cancelar
              </button>
              <button
                type="submit"
                className="btn"
                disabled={loading || !nombre.trim()}
                style={{
                  backgroundColor: 'var(--feature-primary)',
                  color: 'var(--white)',
                  fontWeight: 'var(--font-weight-semibold)'
                }}
              >
                {loading ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2"></span>
                    Creando...
                  </>
                ) : (
                  <>
                    <i className="bi bi-check-lg me-1"></i>
                    Crear Plantilla
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

// ============================================
// COMPONENTE: PlantillaEditor
// ============================================
function PlantillaEditor({ plantilla, loading, onSave, onUploadImage, onBack }) {
  const [campos, setCampos] = useState(plantilla?.campos || []);
  const [selectedCampo, setSelectedCampo] = useState(null);
  const [saving, setSaving] = useState(false);
  const [imageLoading, setImageLoading] = useState(false);

  // Calcular dimensiones de preview dinámicamente
  const previewDims = getPreviewDimensions(plantilla);
  const previewScale = previewDims.scale || PREVIEW_SCALE;
  const esSticker = isSticker(plantilla);

  // Actualizar campos cuando cambia la plantilla
  useEffect(() => {
    setCampos(plantilla?.campos || []);
  }, [plantilla]);

  // Agregar un campo nuevo
  const agregarCampo = (tipo) => {
    const nuevoId = `${tipo}_${Date.now()}`;
    // Diferentes defaults para stickers vs credenciales
    const defaults = esSticker ? {
      qrConsulta: { width: 150, height: 150 }
    } : {
      qrActivacion: { width: 100, height: 100, fontSize: 14 },
      codigoRascable: { width: 150, height: 30, fontSize: 14, fontWeight: 'bold', color: '#000000' },
      folio: { width: 120, height: 40, fontSize: 26, padding: 8 },
      nombreOwner: { width: 200, height: 50, fontSize: 48, color: '#0f0f0f' }
    };
    const nuevoCampo = {
      id: nuevoId,
      tipo,
      x: 50,
      y: 50,
      ...defaults[tipo]
    };
    setCampos(prev => [...prev, nuevoCampo]);
    setSelectedCampo(nuevoId);
  };

  // Actualizar posición/tamaño de un campo
  const updateCampo = (id, updates) => {
    setCampos(prev => prev.map(c =>
      c.id === id ? { ...c, ...updates } : c
    ));
  };

  // Eliminar campo
  const eliminarCampo = (id) => {
    setCampos(prev => prev.filter(c => c.id !== id));
    if (selectedCampo === id) {
      setSelectedCampo(null);
    }
  };

  // Guardar cambios
  const handleSave = async () => {
    setSaving(true);
    await onSave(campos);
    setSaving(false);
  };

  // Subir imagen
  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!['image/png', 'image/jpeg', 'image/webp'].includes(file.type)) {
      alert('Solo se permiten imágenes PNG, JPG o WEBP');
      return;
    }

    setImageLoading(true);
    await onUploadImage(file);
    setImageLoading(false);
  };

  const campoSeleccionado = campos.find(c => c.id === selectedCampo);

  return (
    <div className="plantilla-editor">
      {/* Header */}
      <div
        className="d-flex justify-content-between align-items-center"
        style={{ marginBottom: 'var(--space-4)' }}
      >
        <div className="d-flex align-items-center gap-3">
          <button
            className="btn btn-sm"
            onClick={onBack}
            style={{
              backgroundColor: 'var(--gray-100)',
              color: 'var(--gray-700)'
            }}
          >
            <i className="bi bi-arrow-left"></i>
          </button>
          <h5
            style={{
              margin: 0,
              fontSize: 'var(--font-size-lg)',
              fontWeight: 'var(--font-weight-bold)',
              color: 'var(--gray-900)'
            }}
          >
            <i className="bi bi-pencil me-2" style={{ color: 'var(--feature-primary)' }}></i>
            {plantilla?.nombre || 'Cargando...'}
          </h5>
          <span
            className="badge"
            style={{
              backgroundColor: plantilla?.tipo === 'frente'
                ? 'var(--feature-primary)'
                : 'var(--gray-600)',
              color: 'var(--white)'
            }}
          >
            {plantilla?.tipo}
          </span>
        </div>

        <button
          className="btn"
          onClick={handleSave}
          disabled={saving || loading}
          style={{
            backgroundColor: 'var(--feature-primary)',
            color: 'var(--white)',
            fontWeight: 'var(--font-weight-semibold)'
          }}
        >
          {saving ? (
            <>
              <span className="spinner-border spinner-border-sm me-2"></span>
              Guardando...
            </>
          ) : (
            <>
              <i className="bi bi-check-lg me-1"></i>
              Guardar
            </>
          )}
        </button>
      </div>

      <div className="row">
        {/* Canvas del editor */}
        <div className="col-12 col-lg-8">
          <div
            className="editor-canvas-container"
            style={{
              backgroundColor: 'var(--gray-200)',
              borderRadius: 'var(--radius-lg)',
              padding: 'var(--space-4)',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              minHeight: '400px'
            }}
          >
            <div
              className="editor-canvas"
              style={{
                width: `${previewDims.width}px`,
                height: `${previewDims.height}px`,
                backgroundColor: 'white',
                borderRadius: esSticker && plantilla?.tipo?.includes('round') ? '50%' : 'var(--radius-md)',
                boxShadow: 'var(--shadow-md)',
                position: 'relative',
                overflow: 'hidden',
                backgroundImage: plantilla?.imagenUrl ? `url(${plantilla.imagenUrl})` : 'none',
                backgroundSize: 'cover',
                backgroundPosition: 'center'
              }}
              onClick={() => setSelectedCampo(null)}
            >
              {!plantilla?.imagenUrl && (
                <div
                  style={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    textAlign: 'center',
                    color: 'var(--gray-400)'
                  }}
                >
                  <i className="bi bi-image" style={{ fontSize: '2rem' }}></i>
                  <p style={{ marginTop: 'var(--space-2)' }}>Sube una imagen de fondo</p>
                </div>
              )}

              {/* Campos arrastrables */}
              {campos.map(campo => (
                <Rnd
                  key={campo.id}
                  position={{ x: campo.x * previewScale, y: campo.y * previewScale }}
                  size={{
                    width: campo.width * previewScale,
                    height: campo.height * previewScale
                  }}
                  onDragStop={(e, d) => {
                    e.stopPropagation();
                    updateCampo(campo.id, {
                      x: Math.round(d.x / previewScale),
                      y: Math.round(d.y / previewScale)
                    });
                  }}
                  onResizeStop={(e, direction, ref, delta, position) => {
                    updateCampo(campo.id, {
                      width: Math.round(parseInt(ref.style.width) / previewScale),
                      height: Math.round(parseInt(ref.style.height) / previewScale),
                      x: Math.round(position.x / previewScale),
                      y: Math.round(position.y / previewScale)
                    });
                  }}
                  bounds="parent"
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedCampo(campo.id);
                  }}
                  style={{
                    border: selectedCampo === campo.id
                      ? '2px solid var(--feature-primary)'
                      : '1px dashed var(--gray-400)',
                    backgroundColor: selectedCampo === campo.id
                      ? 'rgba(132, 204, 22, 0.1)'
                      : 'rgba(255, 255, 255, 0.8)',
                    borderRadius: 'var(--radius-sm)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'move'
                  }}
                >
                  {(campo.tipo === 'qrActivacion' || campo.tipo === 'qrConsulta') ? (
                    <div style={{ textAlign: 'center', fontSize: '10px', color: 'var(--gray-600)' }}>
                      <i className="bi bi-qr-code" style={{ fontSize: '1.5rem' }}></i>
                      <div>QR</div>
                    </div>
                  ) : campo.tipo === 'folio' ? (
                    <div
                      style={{
                        backgroundColor: '#0f0f0f',
                        color: '#6ab04c',
                        fontSize: `${(campo.fontSize || 26) * previewScale}px`,
                        fontWeight: 'bold',
                        padding: `${(campo.padding || 8) * previewScale}px`,
                        borderRadius: '4px',
                        fontFamily: 'monospace'
                      }}
                    >
                      C000047
                    </div>
                  ) : campo.tipo === 'nombreOwner' ? (
                    <div
                      style={{
                        fontSize: `${(campo.fontSize || 48) * previewScale}px`,
                        fontWeight: 'bold',
                        color: campo.color || '#0f0f0f',
                        padding: '2px 4px',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden'
                      }}
                    >
                      Juan Pérez
                    </div>
                  ) : (
                    <div
                      style={{
                        fontSize: `${(campo.fontSize || 14) * previewScale}px`,
                        fontWeight: campo.fontWeight || 'bold',
                        color: campo.color || '#000000',
                        padding: '2px 4px'
                      }}
                    >
                      XXXXXX
                    </div>
                  )}
                </Rnd>
              ))}
            </div>
          </div>

          {/* Botón subir imagen */}
          <div className="mt-3 text-center">
            <input
              type="file"
              id="imageUpload"
              accept="image/png,image/jpeg,image/webp"
              onChange={handleImageUpload}
              style={{ display: 'none' }}
            />
            <label
              htmlFor="imageUpload"
              className="btn"
              style={{
                backgroundColor: 'var(--gray-100)',
                color: 'var(--gray-700)',
                cursor: 'pointer'
              }}
            >
              {imageLoading ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2"></span>
                  Subiendo...
                </>
              ) : (
                <>
                  <i className="bi bi-upload me-2"></i>
                  {plantilla?.imagenUrl ? 'Cambiar imagen' : 'Subir imagen PNG'}
                </>
              )}
            </label>
          </div>
        </div>

        {/* Panel lateral */}
        <div className="col-12 col-lg-4 mt-4 mt-lg-0">
          {/* Campos disponibles */}
          <div
            className="card mb-3"
            style={{
              border: '1px solid var(--gray-200)',
              borderRadius: 'var(--radius-lg)'
            }}
          >
            <div
              className="card-header"
              style={{
                backgroundColor: 'var(--gray-50)',
                borderBottom: '1px solid var(--gray-200)',
                fontWeight: 'var(--font-weight-semibold)'
              }}
            >
              <i className="bi bi-plus-square me-2"></i>
              Agregar Campo
              {esSticker && (
                <span className="ms-2" style={{ fontSize: 'var(--font-size-sm)', fontWeight: 'normal' }}>
                  ({campos.length}/16 QRs)
                </span>
              )}
            </div>
            <div className="card-body">
              <div className="d-grid gap-2">
                {/* Campos para STICKERS - solo QR Consulta, máximo 16 */}
                {esSticker && (
                  <button
                    className="btn text-start"
                    onClick={() => agregarCampo('qrConsulta')}
                    disabled={campos.length >= 16}
                    style={{
                      backgroundColor: campos.length >= 16 ? 'var(--gray-200)' : 'var(--gray-100)',
                      color: campos.length >= 16 ? 'var(--gray-400)' : 'var(--gray-700)'
                    }}
                  >
                    <i className="bi bi-qr-code me-2" style={{ color: campos.length >= 16 ? 'var(--gray-400)' : 'var(--feature-primary)' }}></i>
                    {campos.length >= 16 ? 'Máximo 16 QRs alcanzado' : 'Agregar QR Consulta'}
                    <small className="d-block text-muted" style={{ fontSize: '10px' }}>
                      Apunta a /consultar?registro=ID
                    </small>
                  </button>
                )}

                {/* Campos para CREDENCIALES */}
                {!esSticker && (
                  <>
                    <button
                      className="btn text-start"
                      onClick={() => agregarCampo('qrActivacion')}
                      style={{
                        backgroundColor: 'var(--gray-100)',
                        color: 'var(--gray-700)'
                      }}
                    >
                      <i className="bi bi-qr-code me-2" style={{ color: 'var(--feature-primary)' }}></i>
                      QR Activación
                    </button>
                    <button
                      className="btn text-start"
                      onClick={() => agregarCampo('codigoRascable')}
                      style={{
                        backgroundColor: 'var(--gray-100)',
                        color: 'var(--gray-700)'
                      }}
                    >
                      <i className="bi bi-123 me-2" style={{ color: 'var(--feature-primary)' }}></i>
                      Código Rascable
                    </button>
                    <button
                      className="btn text-start"
                      onClick={() => agregarCampo('folio')}
                      style={{
                        backgroundColor: 'var(--gray-100)',
                        color: 'var(--gray-700)'
                      }}
                    >
                      <i className="bi bi-tag-fill me-2" style={{ color: 'var(--feature-primary)' }}></i>
                      Folio (Badge)
                    </button>
                    <button
                      className="btn text-start"
                      onClick={() => agregarCampo('nombreOwner')}
                      style={{
                        backgroundColor: 'var(--gray-100)',
                        color: 'var(--gray-700)'
                      }}
                    >
                      <i className="bi bi-person-fill me-2" style={{ color: 'var(--feature-primary)' }}></i>
                      Nombre Owner
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Propiedades del campo seleccionado */}
          {campoSeleccionado && (
            <div
              className="card"
              style={{
                border: '1px solid var(--gray-200)',
                borderRadius: 'var(--radius-lg)'
              }}
            >
              <div
                className="card-header d-flex justify-content-between align-items-center"
                style={{
                  backgroundColor: 'var(--gray-50)',
                  borderBottom: '1px solid var(--gray-200)',
                  fontWeight: 'var(--font-weight-semibold)'
                }}
              >
                <span>
                  <i className="bi bi-sliders me-2"></i>
                  Propiedades
                </span>
                <button
                  className="btn btn-sm"
                  onClick={() => eliminarCampo(campoSeleccionado.id)}
                  style={{ color: 'var(--color-danger)' }}
                >
                  <i className="bi bi-trash"></i>
                </button>
              </div>
              <div className="card-body">
                <div className="mb-2">
                  <small style={{ color: 'var(--gray-500)' }}>Tipo:</small>
                  <div style={{ fontWeight: 'var(--font-weight-semibold)' }}>
                    {campoSeleccionado.tipo === 'qrActivacion' ? 'QR Activación' :
                     campoSeleccionado.tipo === 'codigoRascable' ? 'Código Rascable' :
                     campoSeleccionado.tipo === 'folio' ? 'Folio (Badge)' :
                     campoSeleccionado.tipo === 'nombreOwner' ? 'Nombre Owner' : campoSeleccionado.tipo}
                  </div>
                </div>

                {/* Nota para nombreOwner */}
                {campoSeleccionado.tipo === 'nombreOwner' && (
                  <div className="alert alert-info py-1 px-2 mb-2" style={{ fontSize: 'var(--font-size-xs)' }}>
                    <i className="bi bi-info-circle me-1"></i>
                    Solo se muestra si el objeto tiene owner
                  </div>
                )}

                <div className="row mb-2">
                  <div className="col-6">
                    <label className="form-label" style={{ fontSize: 'var(--font-size-sm)' }}>X</label>
                    <input
                      type="number"
                      className="form-control form-control-sm"
                      value={campoSeleccionado.x}
                      onChange={e => updateCampo(campoSeleccionado.id, { x: parseInt(e.target.value) || 0 })}
                    />
                  </div>
                  <div className="col-6">
                    <label className="form-label" style={{ fontSize: 'var(--font-size-sm)' }}>Y</label>
                    <input
                      type="number"
                      className="form-control form-control-sm"
                      value={campoSeleccionado.y}
                      onChange={e => updateCampo(campoSeleccionado.id, { y: parseInt(e.target.value) || 0 })}
                    />
                  </div>
                </div>

                <div className="row mb-2">
                  <div className="col-6">
                    <label className="form-label" style={{ fontSize: 'var(--font-size-sm)' }}>Ancho</label>
                    <input
                      type="number"
                      className="form-control form-control-sm"
                      value={campoSeleccionado.width}
                      onChange={e => updateCampo(campoSeleccionado.id, { width: parseInt(e.target.value) || 50 })}
                    />
                  </div>
                  <div className="col-6">
                    <label className="form-label" style={{ fontSize: 'var(--font-size-sm)' }}>Alto</label>
                    <input
                      type="number"
                      className="form-control form-control-sm"
                      value={campoSeleccionado.height}
                      onChange={e => updateCampo(campoSeleccionado.id, { height: parseInt(e.target.value) || 30 })}
                    />
                  </div>
                </div>

                {/* Propiedades para Código Rascable */}
                {campoSeleccionado.tipo === 'codigoRascable' && (
                  <>
                    <div className="mb-2">
                      <label className="form-label" style={{ fontSize: 'var(--font-size-sm)' }}>Tamaño fuente</label>
                      <input
                        type="number"
                        className="form-control form-control-sm"
                        value={campoSeleccionado.fontSize || 14}
                        onChange={e => updateCampo(campoSeleccionado.id, { fontSize: parseInt(e.target.value) || 14 })}
                      />
                    </div>

                    <div className="mb-2">
                      <label className="form-label" style={{ fontSize: 'var(--font-size-sm)' }}>Color</label>
                      <input
                        type="color"
                        className="form-control form-control-sm form-control-color"
                        value={campoSeleccionado.color || '#000000'}
                        onChange={e => updateCampo(campoSeleccionado.id, { color: e.target.value })}
                      />
                    </div>

                    <div className="form-check">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id="fontBold"
                        checked={campoSeleccionado.fontWeight === 'bold'}
                        onChange={e => updateCampo(campoSeleccionado.id, {
                          fontWeight: e.target.checked ? 'bold' : 'normal'
                        })}
                      />
                      <label className="form-check-label" htmlFor="fontBold" style={{ fontSize: 'var(--font-size-sm)' }}>
                        Negrita
                      </label>
                    </div>
                  </>
                )}

                {/* Propiedades para Folio */}
                {campoSeleccionado.tipo === 'folio' && (
                  <>
                    <div className="mb-2">
                      <label className="form-label" style={{ fontSize: 'var(--font-size-sm)' }}>Tamaño fuente</label>
                      <input
                        type="number"
                        className="form-control form-control-sm"
                        value={campoSeleccionado.fontSize || 26}
                        onChange={e => updateCampo(campoSeleccionado.id, { fontSize: parseInt(e.target.value) || 26 })}
                      />
                    </div>

                    <div className="mb-2">
                      <label className="form-label" style={{ fontSize: 'var(--font-size-sm)' }}>Padding</label>
                      <input
                        type="number"
                        className="form-control form-control-sm"
                        value={campoSeleccionado.padding || 8}
                        onChange={e => updateCampo(campoSeleccionado.id, { padding: parseInt(e.target.value) || 8 })}
                      />
                    </div>

                    <small style={{ color: 'var(--gray-500)', display: 'block' }}>
                      Colores fijos: fondo negro, texto verde
                    </small>
                  </>
                )}

                {/* Propiedades para Nombre Owner */}
                {campoSeleccionado.tipo === 'nombreOwner' && (
                  <>
                    <div className="mb-2">
                      <label className="form-label" style={{ fontSize: 'var(--font-size-sm)' }}>Tamaño fuente</label>
                      <input
                        type="number"
                        className="form-control form-control-sm"
                        value={campoSeleccionado.fontSize || 48}
                        onChange={e => updateCampo(campoSeleccionado.id, { fontSize: parseInt(e.target.value) || 48 })}
                      />
                    </div>

                    <div className="mb-2">
                      <label className="form-label" style={{ fontSize: 'var(--font-size-sm)' }}>Color</label>
                      <input
                        type="color"
                        className="form-control form-control-sm form-control-color"
                        value={campoSeleccionado.color || '#0f0f0f'}
                        onChange={e => updateCampo(campoSeleccionado.id, { color: e.target.value })}
                      />
                    </div>
                  </>
                )}
              </div>
            </div>
          )}

          {/* Campos actuales */}
          {campos.length > 0 && (
            <div
              className="card mt-3"
              style={{
                border: '1px solid var(--gray-200)',
                borderRadius: 'var(--radius-lg)'
              }}
            >
              <div
                className="card-header"
                style={{
                  backgroundColor: 'var(--gray-50)',
                  borderBottom: '1px solid var(--gray-200)',
                  fontWeight: 'var(--font-weight-semibold)'
                }}
              >
                <i className="bi bi-list-ul me-2"></i>
                Campos ({campos.length})
              </div>
              <ul className="list-group list-group-flush">
                {campos.map(campo => {
                  const iconMap = {
                    qrActivacion: 'bi-qr-code',
                    codigoRascable: 'bi-123',
                    folio: 'bi-tag-fill',
                    nombreOwner: 'bi-person-fill'
                  };
                  const labelMap = {
                    qrActivacion: 'QR',
                    codigoRascable: 'Código',
                    folio: 'Folio',
                    nombreOwner: 'Nombre'
                  };
                  return (
                    <li
                      key={campo.id}
                      className="list-group-item d-flex justify-content-between align-items-center"
                      style={{
                        cursor: 'pointer',
                        backgroundColor: selectedCampo === campo.id ? 'var(--feature-accent)' : 'transparent'
                      }}
                      onClick={() => setSelectedCampo(campo.id)}
                    >
                      <span style={{ fontSize: 'var(--font-size-sm)' }}>
                        <i className={`bi ${iconMap[campo.tipo] || 'bi-square'} me-2`}></i>
                        {labelMap[campo.tipo] || campo.tipo}
                      </span>
                      <small style={{ color: 'var(--gray-500)' }}>
                        {campo.x}, {campo.y}
                      </small>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ============================================
// COMPONENTE: GenerarCredenciales
// ============================================
function GenerarCredenciales({ plantillas, loading, onGenerar, onBack }) {
  const [plantillaFrenteId, setPlantillaFrenteId] = useState('');
  const [plantillaReversoId, setPlantillaReversoId] = useState('');
  const [foliosText, setFoliosText] = useState('');
  const [generating, setGenerating] = useState(false);
  const [result, setResult] = useState(null);

  const plantillasFrente = plantillas.filter(p => p.tipo === 'frente');
  const plantillasReverso = plantillas.filter(p => p.tipo === 'reverso');

  // Parsear folios del textarea
  const folios = foliosText
    .split('\n')
    .map(f => f.trim())
    .filter(f => f.length > 0);

  // Calcular páginas estimadas
  const paginasEstimadas = folios.length * (
    (plantillaFrenteId ? 1 : 0) + (plantillaReversoId ? 1 : 0)
  );

  const handleGenerar = async () => {
    if (folios.length === 0) {
      alert('Ingresa al menos un folio');
      return;
    }

    if (!plantillaFrenteId && !plantillaReversoId) {
      alert('Selecciona al menos una plantilla');
      return;
    }

    setGenerating(true);
    setResult(null);

    const res = await onGenerar(
      plantillaFrenteId || null,
      plantillaReversoId || null,
      folios
    );

    setResult(res);
    setGenerating(false);
  };

  return (
    <div className="generar-credenciales">
      {/* Header */}
      <div
        className="d-flex align-items-center gap-3"
        style={{ marginBottom: 'var(--space-5)' }}
      >
        <button
          className="btn btn-sm"
          onClick={onBack}
          style={{
            backgroundColor: 'var(--gray-100)',
            color: 'var(--gray-700)'
          }}
        >
          <i className="bi bi-arrow-left"></i>
        </button>
        <h5
          style={{
            margin: 0,
            fontSize: 'var(--font-size-xl)',
            fontWeight: 'var(--font-weight-bold)',
            color: 'var(--gray-900)'
          }}
        >
          <i className="bi bi-file-pdf me-2" style={{ color: 'var(--feature-primary)' }}></i>
          Generar Credenciales
        </h5>
      </div>

      <div className="row">
        <div className="col-12 col-lg-8">
          {/* Selección de plantillas */}
          <div
            className="card mb-4"
            style={{
              border: '1px solid var(--gray-200)',
              borderRadius: 'var(--radius-lg)'
            }}
          >
            <div
              className="card-header"
              style={{
                backgroundColor: 'var(--gray-50)',
                borderBottom: '1px solid var(--gray-200)',
                fontWeight: 'var(--font-weight-semibold)'
              }}
            >
              <i className="bi bi-layout-text-window me-2"></i>
              Seleccionar Plantillas
            </div>
            <div className="card-body">
              <div className="row">
                <div className="col-md-6 mb-3 mb-md-0">
                  <label className="form-label" style={{ fontWeight: 'var(--font-weight-semibold)' }}>
                    Plantilla Frente
                  </label>
                  <select
                    className="form-select"
                    value={plantillaFrenteId}
                    onChange={e => setPlantillaFrenteId(e.target.value)}
                    style={{ borderColor: 'var(--gray-300)' }}
                  >
                    <option value="">-- Ninguna --</option>
                    {plantillasFrente.map(p => (
                      <option key={p.id} value={p.id}>{p.nombre}</option>
                    ))}
                  </select>
                </div>
                <div className="col-md-6">
                  <label className="form-label" style={{ fontWeight: 'var(--font-weight-semibold)' }}>
                    Plantilla Reverso
                  </label>
                  <select
                    className="form-select"
                    value={plantillaReversoId}
                    onChange={e => setPlantillaReversoId(e.target.value)}
                    style={{ borderColor: 'var(--gray-300)' }}
                  >
                    <option value="">-- Ninguna --</option>
                    {plantillasReverso.map(p => (
                      <option key={p.id} value={p.id}>{p.nombre}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          </div>

          {/* Folios */}
          <div
            className="card mb-4"
            style={{
              border: '1px solid var(--gray-200)',
              borderRadius: 'var(--radius-lg)'
            }}
          >
            <div
              className="card-header"
              style={{
                backgroundColor: 'var(--gray-50)',
                borderBottom: '1px solid var(--gray-200)',
                fontWeight: 'var(--font-weight-semibold)'
              }}
            >
              <i className="bi bi-list-ol me-2"></i>
              Números de folio
            </div>
            <div className="card-body">
              <textarea
                className="form-control"
                rows={8}
                value={foliosText}
                onChange={e => setFoliosText(e.target.value)}
                placeholder="Ingresa los números de folio, uno por línea...

Ejemplo:
47
48
49
50"
                style={{
                  borderColor: 'var(--gray-300)',
                  fontFamily: 'monospace',
                  fontSize: 'var(--font-size-sm)'
                }}
              />
              <small style={{ color: 'var(--gray-500)', marginTop: 'var(--space-2)', display: 'block' }}>
                Los números de folio se pueden obtener del panel de administración o del CSV de creación
              </small>
            </div>
          </div>

          {/* Resumen y botón */}
          <div
            className="card"
            style={{
              border: '1px solid var(--gray-200)',
              borderRadius: 'var(--radius-lg)',
              backgroundColor: 'var(--gray-50)'
            }}
          >
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <div style={{ fontSize: 'var(--font-size-lg)', fontWeight: 'var(--font-weight-semibold)' }}>
                    {folios.length} credencial{folios.length !== 1 ? 'es' : ''}
                  </div>
                  <small style={{ color: 'var(--gray-500)' }}>
                    {paginasEstimadas} página{paginasEstimadas !== 1 ? 's' : ''} estimada{paginasEstimadas !== 1 ? 's' : ''}
                  </small>
                </div>

                <button
                  className="btn btn-lg"
                  onClick={handleGenerar}
                  disabled={generating || folios.length === 0 || (!plantillaFrenteId && !plantillaReversoId)}
                  style={{
                    backgroundColor: 'var(--feature-primary)',
                    color: 'var(--white)',
                    fontWeight: 'var(--font-weight-semibold)'
                  }}
                >
                  {generating ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2"></span>
                      Generando...
                    </>
                  ) : (
                    <>
                      <i className="bi bi-download me-2"></i>
                      Generar PDF
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>

          {/* Resultado */}
          {result && (
            <div
              className={`alert ${result.success ? 'alert-success' : 'alert-danger'} mt-4`}
              style={{ borderRadius: 'var(--radius-lg)' }}
            >
              {result.success ? (
                <>
                  <i className="bi bi-check-circle-fill me-2"></i>
                  PDF generado con {result.generadas} credencial{result.generadas !== 1 ? 'es' : ''}.
                  {result.errores > 0 && (
                    <span className="ms-2 text-warning">
                      ({result.errores} error{result.errores !== 1 ? 'es' : ''})
                    </span>
                  )}
                </>
              ) : (
                <>
                  <i className="bi bi-exclamation-triangle-fill me-2"></i>
                  Error: {result.error}
                </>
              )}
            </div>
          )}
        </div>

        {/* Info lateral */}
        <div className="col-12 col-lg-4 mt-4 mt-lg-0">
          <div
            className="card"
            style={{
              border: '1px solid var(--gray-200)',
              borderRadius: 'var(--radius-lg)'
            }}
          >
            <div
              className="card-header"
              style={{
                backgroundColor: 'var(--gray-50)',
                borderBottom: '1px solid var(--gray-200)',
                fontWeight: 'var(--font-weight-semibold)'
              }}
            >
              <i className="bi bi-info-circle me-2"></i>
              Instrucciones
            </div>
            <div className="card-body" style={{ fontSize: 'var(--font-size-sm)' }}>
              <ol style={{ paddingLeft: 'var(--space-4)', margin: 0 }}>
                <li className="mb-2">Selecciona las plantillas de frente y/o reverso</li>
                <li className="mb-2">Ingresa los números de folio (uno por línea)</li>
                <li className="mb-2">Haz clic en "Generar PDF"</li>
                <li className="mb-2">El PDF se descargará automáticamente</li>
                <li>Imprime en tu impresora Fargo</li>
              </ol>

              <hr style={{ borderColor: 'var(--gray-200)' }} />

              <div style={{ color: 'var(--gray-600)' }}>
                <strong>Formato:</strong> CR80 (85.6 × 54 mm)
                <br />
                <strong>Resolución:</strong> 300 DPI
                <br />
                <strong>Máximo:</strong> 100 credenciales por lote
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default CredencialesView;
